import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

interface AdminUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  createdAt: string;
}

interface AdminAuthState {
  user: AdminUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

const initialState: AdminAuthState = {
  user: null,
  loading: false,
  isAuthenticated: false,
  error: null,
};

// Async thunks
export const adminLogin = createAsyncThunk<
  { user: AdminUser; token: string },
  { email: string; password: string },
  { rejectValue: string }
>("adminAuth/login", async ({ email, password }, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.post("/admin/login", {
      email,
      password,
    });

    const { user, token } = response.data;

    // Проверяем, что пользователь действительно администратор
    if (user.role !== "ADMIN") {
      localStorage.removeItem("admin-token");
      throw new Error("Access denied: Admin privileges required");
    }

    localStorage.setItem("admin-token", token);

    return { user, token };
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const adminCheckAuth = createAsyncThunk<
  AdminUser,
  void,
  { rejectValue: string }
>("adminAuth/checkAuth", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get("/admin/me");
    const user = response.data.user;

    // Проверяем, что пользователь действительно администратор
    if (user.role !== "ADMIN") {
      localStorage.removeItem("admin-token");
      throw new Error("Access denied: Admin privileges required");
    }

    return user;
  } catch (error: unknown) {
    localStorage.removeItem("admin-token");
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

const adminAuthSlice = createSlice({
  name: "adminAuth",
  initialState,
  reducers: {
    adminLogout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem("admin-token");
    },
    clearAdminError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(adminLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
        state.isAuthenticated = false;
      })
      // Check auth
      .addCase(adminCheckAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(adminCheckAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(adminCheckAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
      });
  },
});

export const { adminLogout, clearAdminError } = adminAuthSlice.actions;
export default adminAuthSlice.reducer;
